import { CheckCircle2, TriangleAlert } from 'lucide-react'
import { useState } from 'react'

import type { BalanceSheetLine } from '@/api/reports-types'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import { today } from './constants'
import { BranchFilterField } from './filters'
import { REPORTS_PERMISSIONS, useReportsPermissions } from './permissions'
import { useReportBalanceSheet } from './queries'
import { ReportExportButtons } from './report-export-buttons'
import { ReportHeaderNotice } from './report-header-notice'
import { RestrictedPanel } from './restricted-panel'

/**
 * Assets against liabilities and equity, as at a business date.
 *
 * Read from the same trial balance the ledger already holds, restricted to the asset, liability and
 * equity classes and split into the sheet's two sides — never a second summation. The verdict comes
 * first, above the two tables, the same reasoning the trial balance screen gives for its own: a
 * balance sheet that does not say whether it balances is a table of numbers, and the server's own
 * verdict is read as sent rather than recomputed from what happens to be on screen.
 */
export function BalanceSheetSection() {
  const permissions = useReportsPermissions()

  const [asOf, setAsOf] = useState(today)
  const [branchId, setBranchId] = useState<string | null>(null)

  const query = useReportBalanceSheet({ asOf, branchId }, permissions.canViewBalanceSheet && asOf !== '')

  if (!permissions.canViewBalanceSheet) {
    return <RestrictedPanel what="The balance sheet" permission={REPORTS_PERMISSIONS.trialBalanceView} />
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="grid gap-3 p-4 lg:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="bs-as-of">As of</Label>
            <Input id="bs-as-of" type="date" value={asOf} onChange={(event) => setAsOf(event.target.value)} />
          </div>
          <BranchFilterField id="bs-branch" branchId={branchId} onChange={setBranchId} />
        </CardContent>
      </Card>

      {query.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : query.isError ? (
        <ErrorSurface error={query.error} onRetry={() => void query.refetch()} title="The balance sheet could not be read" />
      ) : (
        <>
          <ReportHeaderNotice header={query.data.header} />
          <ReportExportButtons query={{ report: 'BalanceSheet', asOf, branchId }} />

          <Alert tone={query.data.isBalanced ? 'success' : 'danger'}>
            {query.data.isBalanced ? <CheckCircle2 aria-hidden /> : <TriangleAlert aria-hidden />}
            <div className="min-w-0 space-y-1">
              <AlertTitle>
                {query.data.isBalanced ? 'This balance sheet balances' : 'This balance sheet does not balance'}
              </AlertTitle>
              <AlertDescription>
                {query.data.isBalanced
                  ? 'Total assets equal total liabilities plus total equity.'
                  : 'Total assets do not equal total liabilities plus total equity — find it before this is filed anywhere.'}
              </AlertDescription>
              <div className="grid gap-3 sm:grid-cols-3">
                <TotalStat label="Total assets" amount={query.data.totalAssets} />
                <TotalStat label="Total liabilities" amount={query.data.totalLiabilities} />
                <TotalStat label="Total equity" amount={query.data.totalEquity} />
              </div>
            </div>
          </Alert>

          <div className="grid gap-4 lg:grid-cols-2">
            <BalanceSheetTable
              title="Assets"
              lines={query.data.assetLines}
              total={query.data.totalAssets}
              totalLabel="Total assets"
            />
            <BalanceSheetTable
              title="Liabilities and equity"
              lines={query.data.liabilityAndEquityLines}
              total={query.data.totalLiabilitiesAndEquity}
              totalLabel="Total liabilities and equity"
            />
          </div>
        </>
      )}
    </div>
  )
}

function TotalStat({ label, amount }: { label: string; amount: { minor: number; currency: string } }) {
  return (
    <div className="rounded-control border border-border-subtle bg-surface p-3">
      <p className="text-2xs text-muted">{label}</p>
      <MoneyDisplay value={amount.minor} currency={amount.currency} emphasis="strong" className="text-left" />
    </div>
  )
}

function BalanceSheetTable({
  title,
  lines,
  total,
  totalLabel,
}: {
  title: string
  lines: BalanceSheetLine[]
  total: { minor: number; currency: string }
  totalLabel: string
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {lines.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-body">Nothing on this side for this scope.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <tr>
                  <TableHead scope="col">Account</TableHead>
                  <TableHead scope="col" className="text-right">Amount</TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {lines.map((line) => (
                  <TableRow key={line.glAccountCode} className={line.isPostable ? undefined : 'bg-surface-muted/60'}>
                    <TableCell style={{ paddingLeft: `${line.level * 12 + 16}px` }}>
                      <span className={line.isPostable ? 'text-sm' : 'text-sm font-semibold text-heading'}>
                        {line.accountName}
                      </span>
                      <span className="ml-2 font-mono text-2xs text-muted">{line.glAccountCode}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay value={line.amount.minor} currency={line.amount.currency} symbol={false} />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-surface-muted hover:bg-surface-muted">
                  <TableCell className="text-xs font-semibold text-heading">{totalLabel}</TableCell>
                  <TableCell className="text-right">
                    <MoneyDisplay value={total.minor} currency={total.currency} symbol={false} emphasis="strong" />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
